import clsx from 'clsx';
import { memo, type KeyboardEvent, type PointerEvent } from 'react';

import type { MenuRowProps } from '../types';

import { Icon } from '../../Icon/Icon';
import { Text } from '../../Text/Text';
import { TextBlock } from '../../TextBlock/TextBlock';
import { useListboxItemRef } from '../hooks';
import { areRowPropsEqual, preventFocusSteal } from '../utils';
import './MenuRow.css';

export const MenuRow = memo(function MenuRow({
  id,
  index,
  text,
  onSelect,
  isLoading = false,
  secondaryText,
  selected,
  __private,
}: MenuRowProps) {
  const { elementsRef, isKeyboardFocused = false, onHoverIndex } = __private;
  const isToggle = selected !== undefined;
  const ref = useListboxItemRef(elementsRef, index);

  const handleSelect = () => {
    if (!isLoading) {
      onSelect();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  const handleMouseMove = () => {
    onHoverIndex(index);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') {
      onHoverIndex(null);
    }
  };

  return (
    <div
      aria-busy={isLoading || undefined}
      aria-disabled={isLoading || undefined}
      aria-selected={selected}
      className={clsx(
        'eds-select--menu-row',
        isKeyboardFocused && 'eds-select--menu-row-keyboard-focus',
        isToggle && 'eds-select--menu-row--toggle',
        selected && 'eds-select--menu-row--selected',
        isLoading && 'eds-select--menu-row-loading',
      )}
      id={id}
      ref={ref}
      role="option"
      tabIndex={-1}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      onMouseDown={preventFocusSteal}
      onMouseMove={handleMouseMove}
      onPointerLeave={handlePointerLeave}
    >
      {isToggle ? (
        <TextBlock
          secondaryText={secondaryText}
          secondaryTone={selected ? 'auto' : 'subdued'}
          text={text}
          truncate={1}
        />
      ) : (
        <Text truncate={1} variant="body-semi-m">
          {text}
        </Text>
      )}
      {selected ? (
        <span className="eds-select--menu-row-check">
          <Icon icon="check" size="s" />
        </span>
      ) : null}
      {isLoading ? <Icon icon="loading" size="s" /> : null}
    </div>
  );
}, areRowPropsEqual);

MenuRow.displayName = 'MenuRow';
